import { SearchMode } from 'components/Search/SearchModal';

export type ScannerConfig = {
    action: ScannerAction['action'];
    labelKey: string;
    category: string;
    scannerValue: ScannerAction;
    requiresDoubleConfirm: boolean;
    requiresDialogConfirm?: boolean; // // When true: 2nd scan opens a dialog, 3rd scan confirms it
    requiresReturn?: boolean;
    showInControl?: boolean;
};

export type ScannerAction =
    | { action: 'completeReturn' }
    | { action: 'saveReturn' }
    | { action: 'goBackToOrderList' }
    | { action: 'newReturn' }
    | { action: 'setMaxQty' }
    | { action: 'deleteReturn' }
    | { action: 'pauseReturn' }
    | { action: 'incomingPackage' }
    | { action: 'goodsReceived' }
    | { action: 'setToInProgress' }
    | { action: 'createReturn' }
    | { action: 'cancelAction' } // Special action used to reset scanner state
    | { action: 'confirmNavigation' } // Confirms the "leave without saving" dialog
    | { action: 'bulkReason'; value: number }
    | { action: 'openModal'; searchMode: SearchMode }; // Opens the search modal with the specified mode

export const SCANNER_CONFIGS: ScannerConfig[] = [
    {
        action: 'completeReturn',
        labelKey: 'configActions.completeReturn',
        category: 'returnActions',
        scannerValue: { action: 'completeReturn' },
        requiresDoubleConfirm: true,
        requiresReturn: true,
    },
    {
        action: 'saveReturn',
        labelKey: 'configActions.saveReturn',
        category: 'returnActions',
        scannerValue: { action: 'saveReturn' },
        requiresDoubleConfirm: false,
        requiresReturn: true,
    },
    {
        action: 'goBackToOrderList',
        labelKey: 'configActions.goBackToOrderList',
        category: 'navigation',
        scannerValue: { action: 'goBackToOrderList' },
        requiresDoubleConfirm: false,
    },
    {
        action: 'newReturn',
        labelKey: 'configActions.newReturn',
        category: 'navigation',
        scannerValue: { action: 'newReturn' },
        requiresDoubleConfirm: false,
    },
    {
        action: 'setMaxQty',
        labelKey: 'configActions.setMaxQty',
        category: 'quantity',
        scannerValue: { action: 'setMaxQty' },
        requiresDoubleConfirm: true,
        requiresReturn: true,
    },
    {
        action: 'deleteReturn',
        labelKey: 'configActions.deleteReturn',
        category: 'returnActions',
        scannerValue: { action: 'deleteReturn' },
        requiresDoubleConfirm: true,
        requiresDialogConfirm: true,
        requiresReturn: true,
    },
    {
        action: 'pauseReturn',
        labelKey: 'configActions.pauseReturn',
        category: 'returnActions',
        scannerValue: { action: 'pauseReturn' },
        requiresDoubleConfirm: true,
        requiresReturn: true,
    },
    {
        action: 'incomingPackage',
        labelKey: 'configActions.incomingPackage',
        category: 'logistics',
        scannerValue: { action: 'incomingPackage' },
        requiresDoubleConfirm: false,
        requiresReturn: true,
    },
    {
        action: 'goodsReceived',
        labelKey: 'configActions.goodsReceived',
        category: 'logistics',
        scannerValue: { action: 'goodsReceived' },
        requiresDoubleConfirm: false,
        requiresReturn: true,
    },
    {
        action: 'setToInProgress',
        labelKey: 'configActions.setToInProgress',
        category: 'returnActions',
        scannerValue: { action: 'setToInProgress' },
        requiresDoubleConfirm: false,
        requiresReturn: true,
    },
    {
        action: 'createReturn',
        labelKey: 'configActions.createReturn',
        category: 'returnActions',
        scannerValue: { action: 'createReturn' },
        requiresDoubleConfirm: false,
        requiresReturn: true,
    },
    {
        action: 'cancelAction',
        labelKey: 'configActions.cancelAction',
        category: 'returnActions',
        scannerValue: { action: 'cancelAction' },
        requiresDoubleConfirm: false,
    },
    {
        action: 'confirmNavigation',
        labelKey: 'configActions.confirmNavigation',
        category: 'navigation',
        scannerValue: { action: 'confirmNavigation' },
        requiresDoubleConfirm: false,
        showInControl: false,
    },
    {
        action: 'bulkReason',
        labelKey: 'configActions.bulkReason',
        category: 'returnActions',
        scannerValue: { action: 'bulkReason', value: 0 }, // 0 is a placeholder
        requiresDoubleConfirm: true,
        requiresDialogConfirm: false,
        requiresReturn: true,
        showInControl: false,
    },
    {
        action: 'openModal',
        labelKey: 'configActions.openModal',
        category: 'navigation',
        scannerValue: { action: 'openModal', searchMode: 'barcode' }, // default to barcode, can be overridden
        requiresDoubleConfirm: false,
        requiresReturn: true,
        showInControl: true,
    },
];

export const parseScannerAction = (raw: string): ScannerAction | null => {
    try {
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed.action !== 'string') return null;
        switch (parsed.action) {
            case 'completeReturn':
                return { action: 'completeReturn' };
            case 'saveReturn':
                return { action: 'saveReturn' };
            case 'goBackToOrderList':
                return { action: 'goBackToOrderList' };
            case 'newReturn':
                return { action: 'newReturn' };
            case 'setMaxQty':
                return { action: 'setMaxQty' };
            case 'deleteReturn':
                return { action: 'deleteReturn' };
            case 'pauseReturn':
                return { action: 'pauseReturn' };
            case 'incomingPackage':
                return { action: 'incomingPackage' };
            case 'goodsReceived':
                return { action: 'goodsReceived' };
            case 'setToInProgress':
                return { action: 'setToInProgress' };
            case 'createReturn':
                return { action: 'createReturn' };
            case 'cancelAction':
                return { action: 'cancelAction' };
            case 'confirmNavigation':
                return { action: 'confirmNavigation' };
            case 'bulkReason':
                return { action: 'bulkReason', value: parsed.value };
            case 'openModal':
                return {
                    action: 'openModal',
                    searchMode:
                        parsed.searchMode === 'orderNumber' ||
                        parsed.searchMode === 'articleNumber' ||
                        parsed.searchMode === 'barcode'
                            ? parsed.searchMode
                            : 'barcode',
                };
            default:
                return null;
        }
    } catch {
        return null;
    }
};
